import { useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  ChevronDown,
  Search,
  Megaphone,
  Coins,
  User,
  LogIn,
  ChevronRight,
  Store,
  LogOut,
  Settings,
  Wallet,
  FileText,
  Loader2,
  Tag,
  MessageSquare,
} from 'lucide-react';
import { AuthModal } from './AuthModal';
import { LucideIcon } from './LucideIcon';
import {
  useAppSelector,
  useAppDispatch,
  selectIsAuthenticated,
  selectCurrentUser,
  selectAuthWallet,
  useLogoutMutation,
  useGetCategoryTreeQuery,
  useGetUnreadCountQuery,
  useGetMeQuery,
  authActions,
} from '@nimbazaar/store';
import { useToast } from './Toast';

const CATEGORY_COLORS = [
  { text: 'text-blue-500', bg: 'bg-blue-50' },
  { text: 'text-emerald-500', bg: 'bg-emerald-50' },
  { text: 'text-pink-500', bg: 'bg-pink-50' },
  { text: 'text-green-500', bg: 'bg-green-50' },
  { text: 'text-orange-500', bg: 'bg-orange-50' },
  { text: 'text-purple-500', bg: 'bg-purple-50' },
  { text: 'text-amber-600', bg: 'bg-amber-50' },
  { text: 'text-red-500', bg: 'bg-red-50' },
  { text: 'text-cyan-500', bg: 'bg-cyan-50' },
  { text: 'text-slate-600', bg: 'bg-slate-100' },
];

export function Navbar() {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const toast = useToast();

  // Redux state
  const isAuthenticated = useAppSelector(selectIsAuthenticated);
  const user = useAppSelector(selectCurrentUser);
  const wallet = useAppSelector(selectAuthWallet);

  const [logout] = useLogoutMutation();
  // Keep wallet in sync — refetches when 'User' tag is invalidated or window regains focus
  useGetMeQuery(undefined, { skip: !isAuthenticated, refetchOnFocus: true });
  const { data: treeData, isLoading: isCategoriesLoading } = useGetCategoryTreeQuery();
  const { data: unreadData } = useGetUnreadCountQuery(undefined, {
    pollingInterval: 30000,
    skip: !isAuthenticated,
  });
  const unreadCount = unreadData?.data?.count ?? 0;
  const categories = treeData?.data ?? [];

  const [searchQuery, setSearchQuery] = useState('');
  const [isCategoryOpen, setIsCategoryOpen] = useState(false);
  const [hoveredCategory, setHoveredCategory] = useState<string | null>(null);
  const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);
  const [isProfileMenuOpen, setIsProfileMenuOpen] = useState(false);

  const hoveredCat = categories.find((c) => c.id === hoveredCategory);

  // Navigate to ads listing filtered by category (and include search text if any)
  const searchByCategory = (categoryId: string) => {
    const params = new URLSearchParams();
    params.set('categoryId', categoryId);
    if (searchQuery.trim()) params.set('search', searchQuery.trim());
    navigate(`/ads?${params.toString()}`);
    setSearchQuery('');
    setIsCategoryOpen(false);
  };

  const profileMenuRef = useRef<HTMLDivElement>(null);

  // Close profile menu when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (profileMenuRef.current && !profileMenuRef.current.contains(event.target as Node)) {
        setIsProfileMenuOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLogout = async () => {
    try {
      await logout().unwrap();
    } catch {
      // Continue with logout even if API fails
    }
    dispatch(authActions.logout());
    setIsProfileMenuOpen(false);
    toast.success('Logged out', 'You have been logged out successfully');
    navigate('/');
  };

  return (
    <header className="w-full sticky top-0 z-50">
      {/* Top Bar - Hidden on mobile */}
      <div className="hidden md:block bg-primary-800 text-white">
        <div className="max-w-7xl mx-auto px-4 py-2 flex items-center justify-between text-sm">
          <span>Download NimBazaar desktop app now</span>
          <nav className="flex items-center gap-6">
            <Link to="/support" className="hover:text-secondary-300 transition-colors">Contact Us / Support</Link>
          </nav>
        </div>
      </div>

      {/* Main Navbar */}
      <div className="bg-primary-500 text-white">
        <div className="max-w-7xl mx-auto px-3 sm:px-4 py-2 sm:py-3">
          {/* Row 1: Logo + Actions (mobile) | Logo + Search + Actions (desktop) */}
          <div className="flex items-center justify-between gap-2 sm:gap-4 md:gap-6">
            {/* Logo */}
            <Link to="/" className="shrink-0 relative z-10" style={{ marginTop: '-8px', marginBottom: '-28px' }}>
              <img src="/logo.png" alt="NimBazaar" className="h-12 sm:h-20 w-auto drop-shadow-lg" />
            </Link>

            {/* Search Bar with Category Dropdown - hidden on mobile, shown on sm+ */}
            <div className="hidden sm:flex items-center w-full max-w-2xl mx-auto">
              {/* Category Dropdown */}
              <div className="relative z-50">
                <button
                  onClick={() => {
                    setIsCategoryOpen(!isCategoryOpen);
                    if (categories.length > 0) {
                      setHoveredCategory(categories[0].id);
                    }
                  }}
                  className="flex items-center gap-2 bg-primary-800 hover:bg-primary-900 text-white px-3 h-10 sm:h-9 rounded-l-md transition-colors"
                >
                  {isCategoriesLoading ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : null}
                  <span className="text-sm whitespace-nowrap max-w-[140px] truncate hidden sm:inline">All Categories</span>
                  <span className="text-sm sm:hidden">All</span>
                  <ChevronDown className={`w-4 h-4 transition-transform ${isCategoryOpen ? 'rotate-180' : ''}`} />
                </button>

                {/* Mega Menu Dropdown - Desktop */}
                {isCategoryOpen && (
                  <>
                    {/* Mobile: Full-width scrollable dropdown */}
                    <div className="sm:hidden fixed left-2 right-2 top-[100px] max-h-[70vh] bg-white rounded-xl shadow-2xl border border-gray-100 z-50 overflow-y-auto">
                      <h3 className="px-4 pt-3 pb-1 text-[11px] font-semibold text-gray-400 uppercase tracking-wider sticky top-0 bg-white">Categories</h3>
                      {categories.map((category, index) => {
                        const color = CATEGORY_COLORS[index % CATEGORY_COLORS.length];
                        return (
                          <div key={category.id}>
                            <button
                              onClick={() => searchByCategory(category.id)}
                              className="w-full text-left px-4 py-3 text-sm transition-all flex items-center gap-3 hover:bg-gray-50"
                            >
                              <div className={`w-8 h-8 rounded-lg ${color.bg} flex items-center justify-center`}>
                                <LucideIcon
                                  name={category.icon || ''}
                                  className={`w-4 h-4 ${color.text}`}
                                  fallback={<Tag className={`w-4 h-4 ${color.text}`} />}
                                />
                              </div>
                              <span className="font-medium flex-1 text-gray-900">{category.name}</span>
                              <ChevronRight className="w-3.5 h-3.5 text-gray-300" />
                            </button>
                            {category.children.length > 0 && (
                              <div className="pl-14 pr-4 pb-2 space-y-0.5">
                                {category.children.map((sub) => (
                                  <button
                                    key={sub.id}
                                    onClick={() => searchByCategory(sub.id)}
                                    className="w-full text-left px-2 py-2 text-sm text-gray-500 hover:text-primary-600 hover:bg-primary-50 rounded-md transition-colors"
                                  >
                                    {sub.name}
                                  </button>
                                ))}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>

                    {/* Desktop: Original mega menu */}
                    <div className="hidden sm:block fixed left-1/2 -translate-x-1/2 top-24 w-[72vw] max-w-5xl bg-white rounded-2xl shadow-2xl border border-gray-100 z-50 overflow-hidden">
                      <div className="flex">
                        {/* Main Categories - Left Side */}
                        <div className="w-60 bg-gray-50 border-r border-gray-200 py-2 overflow-y-auto">
                          <h3 className="px-4 pt-2 pb-1 text-[11px] font-semibold text-gray-400 uppercase tracking-wider">Categories</h3>

                          {categories.map((category, index) => {
                            const color = CATEGORY_COLORS[index % CATEGORY_COLORS.length];
                            const isHovered = hoveredCategory === category.id;
                            return (
                            <button
                              key={category.id}
                              onClick={() => searchByCategory(category.id)}
                              onMouseEnter={() => setHoveredCategory(category.id)}
                              className={`w-full text-left px-3 py-2.5 text-sm transition-all flex items-center gap-3 ${
                                isHovered
                                  ? 'text-gray-900 bg-white shadow-sm border-r-[3px] border-primary-500'
                                  : 'text-gray-600 hover:bg-white/70'
                              }`}
                            >
                              <div className={`w-8 h-8 rounded-lg ${isHovered ? color.bg : 'bg-gray-100'} flex items-center justify-center transition-all ${isHovered ? 'scale-110' : ''}`}>
                                <LucideIcon
                                  name={category.icon || ''}
                                  className={`w-4 h-4 ${isHovered ? color.text : 'text-gray-400'}`}
                                  fallback={<Tag className={`w-4 h-4 ${isHovered ? color.text : 'text-gray-400'}`} />}
                                />
                              </div>
                              <span className={`font-medium flex-1 ${isHovered ? 'text-gray-900' : ''}`}>{category.name}</span>
                              <ChevronRight className={`w-3.5 h-3.5 transition-all ${isHovered ? 'translate-x-0.5 text-primary-500' : 'text-gray-300'}`} />
                            </button>
                            );
                          })}
                        </div>

                        {/* Subcategories - Right Side */}
                        <div className="flex-1 p-6 bg-white overflow-y-auto">
                          {hoveredCat ? (
                            (() => {
                              const hoveredIndex = categories.findIndex((c) => c.id === hoveredCat.id);
                              const hColor = CATEGORY_COLORS[hoveredIndex % CATEGORY_COLORS.length];
                              return (
                            <div>
                              <div className="flex items-center gap-3 mb-5 pb-4 border-b border-gray-100">
                                <div className={`w-11 h-11 rounded-xl ${hColor.bg} flex items-center justify-center`}>
                                  <LucideIcon
                                    name={hoveredCat.icon || ''}
                                    className={`w-5 h-5 ${hColor.text}`}
                                    fallback={<Tag className={`w-5 h-5 ${hColor.text}`} />}
                                  />
                                </div>
                                <div>
                                  <h3 className="font-bold text-lg text-gray-900">{hoveredCat.name}</h3>
                                  {hoveredCat.description && (
                                    <p className="text-xs text-gray-500 mt-0.5">{hoveredCat.description}</p>
                                  )}
                                </div>
                              </div>
                              {hoveredCat.children.length > 0 ? (
                                <div className="grid grid-cols-3 gap-x-3 gap-y-1">
                                  {hoveredCat.children.map((sub) => (
                                    <button
                                      key={sub.id}
                                      onClick={() => searchByCategory(sub.id)}
                                      className="text-left px-3 py-2.5 text-sm text-gray-600 hover:text-primary-600 hover:bg-primary-50 rounded-lg transition-colors group flex items-center gap-2"
                                    >
                                      <LucideIcon
                                        name={sub.icon || ''}
                                        className="w-4 h-4 text-gray-400 group-hover:text-primary-500 transition-colors shrink-0"
                                        fallback={<ChevronRight className="w-3.5 h-3.5 text-gray-300 group-hover:text-primary-400 transition-colors shrink-0" />}
                                      />
                                      <span>{sub.name}</span>
                                    </button>
                                  ))}
                                </div>
                              ) : (
                                <p className="text-sm text-gray-400 py-4">No subcategories yet</p>
                              )}
                              <div className="mt-5 pt-3 border-t border-gray-100">
                                <button
                                  onClick={() => {
                                    setIsCategoryOpen(false);
                                    navigate(`/category/${hoveredCat.slug}`);
                                  }}
                                  className={`${hColor.text} hover:opacity-80 text-sm font-medium flex items-center gap-1.5 group`}
                                >
                                  View all in {hoveredCat.name}
                                  <ChevronRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                                </button>
                              </div>
                            </div>
                              );
                            })()
                          ) : (
                            <div className="flex flex-col items-center justify-center h-full text-gray-400 py-12">
                              <Store className="w-12 h-12 mb-3 text-gray-200" />
                              <p className="text-sm font-medium text-gray-400">Hover a category to explore</p>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </>
                )}
              </div>

              {/* Search Input */}
              <input
                type="text"
                placeholder="Search for wanted ads..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    const params = new URLSearchParams();
                    if (searchQuery.trim()) params.set('search', searchQuery.trim());
                    const qs = params.toString();
                    navigate(`/ads${qs ? `?${qs}` : ''}`);
                    setSearchQuery('');
                  }
                }}
                className="w-full px-4 h-10 sm:h-9 text-sm text-gray-900 placeholder-gray-400 focus:outline-none bg-white"
              />

              {/* Search Button */}
              <button
                onClick={() => {
                  const params = new URLSearchParams();
                  if (searchQuery.trim()) params.set('search', searchQuery.trim());
                  const qs = params.toString();
                  navigate(`/ads${qs ? `?${qs}` : ''}`);
                  setSearchQuery('');
                }}
                className="bg-primary-800 hover:bg-primary-900 px-4 h-10 sm:h-9 rounded-r-md transition-colors"
              >
                <Search className="w-4 h-4" />
              </button>
            </div>

            {/* Right Section */}
            <div className="flex items-center gap-1.5 sm:gap-2">
              {/* Credits/Wallet */}
              <div className="relative group">
                <button
                  onClick={() => isAuthenticated && navigate('/account/wallet')}
                  className="flex items-center gap-1 sm:gap-2 bg-primary-800 hover:bg-primary-900 px-2 sm:px-3 h-8 sm:h-9 rounded-md transition-colors"
                >
                  <Coins className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                  <span className="text-xs sm:text-sm font-semibold">{wallet?.balance || 0}</span>
                </button>
                {/* Hover Tooltip - hidden on mobile to avoid overflow */}
                {!isAuthenticated && (
                  <div className="hidden md:block absolute top-full right-0 mt-2 w-80 bg-white rounded-xl shadow-xl border border-gray-100 p-3 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50">
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 bg-secondary-100 rounded-full flex items-center justify-center shrink-0">
                        <Coins className="w-4 h-4 text-secondary-500" />
                      </div>
                      <p className="text-sm text-gray-600">Sign up and receive <span className="font-bold text-secondary-500">100 free coins</span>!</p>
                      <button
                        onClick={() => setIsAuthModalOpen(true)}
                        className="shrink-0 bg-secondary-500 hover:bg-secondary-600 text-white text-xs font-medium px-3 py-1.5 rounded-lg transition-colors"
                      >
                        Sign Up
                      </button>
                    </div>
                    {/* Arrow */}
                    <div className="absolute -top-2 right-4 w-4 h-4 bg-white border-l border-t border-gray-100 transform rotate-45"></div>
                  </div>
                )}
              </div>

              {isAuthenticated ? (
                <>
                  {/* Messages */}
                  <a
                    href="/inbox"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="relative flex items-center gap-1.5 sm:gap-2 bg-primary-600 hover:bg-primary-700 px-2.5 sm:px-3 h-8 sm:h-9 rounded-md transition-colors"
                  >
                    <MessageSquare className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                    <span className="text-xs sm:text-sm font-medium hidden sm:block">Messages</span>
                    {unreadCount > 0 && (
                      <span className="absolute -top-2 -right-2 bg-red-500 text-white text-[10px] font-bold rounded-full min-w-[20px] h-[20px] flex items-center justify-center px-1 border-2 border-primary-500 shadow-sm">
                        {unreadCount > 99 ? '99+' : unreadCount}
                      </span>
                    )}
                  </a>

                  {/* Profile Dropdown */}
                  <div className="relative" ref={profileMenuRef}>
                    <button
                      onClick={() => setIsProfileMenuOpen(!isProfileMenuOpen)}
                      className="flex items-center hover:bg-primary-600 rounded-full transition-colors p-0.5"
                    >
                      <div className="w-7 h-7 sm:w-9 sm:h-9 bg-white rounded-full flex items-center justify-center border-2 border-white/80 overflow-hidden">
                        {user?.avatar ? (
                          <img src={user.avatar} alt={user.name} className="w-full h-full object-cover" />
                        ) : (
                          <User className="w-5 h-5 text-primary-500" />
                        )}
                      </div>
                    </button>

                    {/* Profile Menu */}
                    {isProfileMenuOpen && (
                      <div className="absolute top-full right-0 mt-2 w-56 sm:w-64 bg-white rounded-xl shadow-xl border border-gray-100 overflow-hidden z-50">
                        {/* User Info */}
                        <div className="p-4 border-b border-gray-100 bg-gray-50">
                          <p className="font-semibold text-gray-900">{user?.name}</p>
                          <p className="text-sm text-gray-500 truncate">{user?.email}</p>
                        </div>

                        {/* Menu Items */}
                        <div className="py-2">
                          <Link
                            to="/account"
                            onClick={() => setIsProfileMenuOpen(false)}
                            className="flex items-center gap-3 px-4 py-2.5 text-gray-700 hover:bg-gray-50 transition-colors"
                          >
                            <User className="w-4 h-4 text-gray-400" />
                            <span className="text-sm">My Profile</span>
                          </Link>
                          <Link
                            to="/account"
                            onClick={() => setIsProfileMenuOpen(false)}
                            className="flex items-center gap-3 px-4 py-2.5 text-gray-700 hover:bg-gray-50 transition-colors"
                          >
                            <Wallet className="w-4 h-4 text-gray-400" />
                            <span className="text-sm">Wallet</span>
                            <span className="ml-auto text-xs font-semibold text-secondary-500">{wallet?.balance || 0} credits</span>
                          </Link>
                          <Link
                            to="/account"
                            onClick={() => setIsProfileMenuOpen(false)}
                            className="flex items-center gap-3 px-4 py-2.5 text-gray-700 hover:bg-gray-50 transition-colors"
                          >
                            <FileText className="w-4 h-4 text-gray-400" />
                            <span className="text-sm">My Ads</span>
                          </Link>
                          <Link
                            to="/account"
                            onClick={() => setIsProfileMenuOpen(false)}
                            className="flex items-center gap-3 px-4 py-2.5 text-gray-700 hover:bg-gray-50 transition-colors"
                          >
                            <Settings className="w-4 h-4 text-gray-400" />
                            <span className="text-sm">Settings</span>
                          </Link>
                        </div>

                        {/* Logout */}
                        <div className="border-t border-gray-100 py-2">
                          <button
                            onClick={handleLogout}
                            className="flex items-center gap-3 px-4 py-2.5 text-red-600 hover:bg-red-50 transition-colors w-full"
                          >
                            <LogOut className="w-4 h-4" />
                            <span className="text-sm">Logout</span>
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </>
              ) : (
                /* Login Button */
                <button
                  onClick={() => setIsAuthModalOpen(true)}
                  className="flex items-center gap-1.5 sm:gap-2 bg-secondary-500 hover:bg-secondary-600 px-2.5 sm:px-4 h-8 sm:h-9 rounded-md transition-colors font-medium"
                >
                  <LogIn className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                  <span className="text-xs sm:text-sm">Login</span>
                </button>
              )}
            </div>
          </div>

        </div>
      </div>

      {/* Click outside to close dropdown */}
      {isCategoryOpen && (
        <div
          className="fixed inset-0 z-40 bg-transparent"
          onClick={() => setIsCategoryOpen(false)}
        />
      )}

      {/* Auth Modal */}
      <AuthModal
        isOpen={isAuthModalOpen}
        onClose={() => setIsAuthModalOpen(false)}
      />
    </header>
  );
}
